import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ReplayIcon from "@mui/icons-material/Replay";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import {
  Box,
  Button,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { UploadJob, useUploadManager } from "./UploadManager";

type UploadStatusPanelViewProps = {
  jobs: UploadJob[];
  onCancel: (jobId: string) => void;
  onRetry: (jobId: string) => void;
  onDismiss: (jobId: string) => void;
};

const formatMegabytes = (bytes: number) => {
  const megabytes = bytes / (1024 * 1024);
  return new Intl.NumberFormat("es-ES", {
    maximumFractionDigits: 1,
  }).format(megabytes);
};

const statusTitle = (job: UploadJob) => {
  switch (job.status) {
    case "queued":
      return "Esperando para subir";
    case "uploading":
      return "Subiendo contenido";
    case "processing":
      return "Finalizando en el servidor";
    case "completed":
      return "Contenido subido";
    case "cancelled":
      return "Subida cancelada";
    case "error":
      return "No se pudo completar";
    case "uncertain":
      return "Resultado pendiente de comprobar";
  }
};

const statusIcon = (job: UploadJob) => {
  if (job.status === "completed") {
    return <CheckCircleOutlineIcon color="success" fontSize="small" />;
  }
  if (job.status === "error" || job.status === "uncertain") {
    return <ErrorOutlineIcon color="error" fontSize="small" />;
  }
  if (job.status === "cancelled") {
    return <CancelOutlinedIcon color="disabled" fontSize="small" />;
  }
  return <CloudUploadOutlinedIcon color="primary" fontSize="small" />;
};

export function UploadStatusPanelView({
  jobs,
  onCancel,
  onRetry,
  onDismiss,
}: UploadStatusPanelViewProps) {
  const [minimized, setMinimized] = useState(false);
  const pending = jobs.filter(
    (job) =>
      job.status === "queued" ||
      job.status === "uploading" ||
      job.status === "processing"
  );
  const active = jobs.find(
    (job) => job.status === "uploading" || job.status === "processing"
  );
  const queuedCount = jobs.filter((job) => job.status === "queued").length;

  if (jobs.length === 0) return null;

  if (minimized) {
    const percentage = active?.progress == null
      ? ""
      : ` · ${Math.round(active.progress * 100)} %`;
    return (
      <Paper sx={panelSx} elevation={8} role="status" aria-live="polite">
        <Stack direction="row" alignItems="center" gap={1} px={1.5} py={1}>
          <CloudUploadOutlinedIcon color="primary" />
          <Typography variant="body2" fontWeight={700} flex={1}>
            {pending.length > 0
              ? `Subidas en curso${percentage}`
              : "Historial de subidas"}
          </Typography>
          <IconButton
            size="small"
            aria-label="Mostrar subidas"
            onClick={() => setMinimized(false)}
          >
            <ExpandLessIcon />
          </IconButton>
        </Stack>
        {active ? (
          <LinearProgress
            variant={active.progress == null ? "indeterminate" : "determinate"}
            value={(active.progress ?? 0) * 100}
          />
        ) : null}
      </Paper>
    );
  }

  return (
    <Paper
      sx={panelSx}
      elevation={8}
      aria-label="Estado de las subidas"
      role="status"
      aria-live="polite"
    >
      <Stack
        direction="row"
        alignItems="center"
        px={2}
        py={1.25}
        borderBottom="1px solid"
        borderColor="divider"
      >
        <Box flex={1}>
          <Typography fontWeight={800}>Subidas multimedia</Typography>
          {queuedCount > 0 ? (
            <Typography variant="caption" color="text.secondary">
              {queuedCount} {queuedCount === 1 ? "subida" : "subidas"} en cola
            </Typography>
          ) : null}
        </Box>
        <IconButton
          size="small"
          aria-label="Minimizar subidas"
          onClick={() => setMinimized(true)}
        >
          <ExpandMoreIcon />
        </IconButton>
      </Stack>

      <Stack maxHeight={420} overflow="auto">
        {jobs.map((job) => {
          const percentage =
            job.progress == null ? null : Math.round(job.progress * 100);
          const canCancel =
            job.status === "queued" || job.status === "uploading";
          const canRetry =
            job.status === "error" || job.status === "cancelled";
          const canDismiss =
            job.status === "completed" ||
            job.status === "error" ||
            job.status === "uncertain" ||
            job.status === "cancelled";
          const jobDescription = `${job.request.userLabel}, semana ${job.request.semana}`;

          return (
            <Box
              key={job.id}
              px={2}
              py={1.5}
              borderBottom="1px solid"
              borderColor="divider"
              sx={{ "&:last-child": { borderBottom: 0 } }}
            >
              <Stack direction="row" alignItems="flex-start" gap={1}>
                <Box mt={0.25}>{statusIcon(job)}</Box>
                <Box minWidth={0} flex={1}>
                  <Typography variant="body2" fontWeight={800}>
                    {statusTitle(job)}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                    noWrap
                  >
                    {job.request.userLabel} · Semana {job.request.semana}
                  </Typography>
                </Box>
                {canCancel ? (
                  <IconButton
                    size="small"
                    aria-label={`Cancelar subida de ${jobDescription}`}
                    onClick={() => onCancel(job.id)}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                ) : null}
                {canDismiss ? (
                  <IconButton
                    size="small"
                    aria-label={`Cerrar subida de ${jobDescription}`}
                    onClick={() => onDismiss(job.id)}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                ) : null}
              </Stack>

              {job.status === "uploading" ? (
                <Box mt={1.25}>
                  <LinearProgress
                    variant={
                      job.progress == null ? "indeterminate" : "determinate"
                    }
                    value={(job.progress ?? 0) * 100}
                    aria-label={`Progreso de la subida de ${job.request.userLabel}`}
                  />
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                    mt={0.75}
                  >
                    {percentage == null
                      ? "Calculando progreso…"
                      : `${formatMegabytes(job.loaded)} MB de ${formatMegabytes(
                          job.total
                        )} MB · ${percentage} %`}
                  </Typography>
                </Box>
              ) : null}

              {job.status === "processing" ? (
                <Box mt={1.25}>
                  <LinearProgress
                    aria-label={`Procesando la subida de ${job.request.userLabel}`}
                  />
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                    mt={0.75}
                  >
                    Los archivos ya se enviaron. Espera a que termine el proceso.
                  </Typography>
                </Box>
              ) : null}

              {job.error ? (
                <Typography variant="caption" color="error" display="block" mt={1}>
                  {job.error}
                </Typography>
              ) : null}

              {canRetry ? (
                <Button
                  size="small"
                  startIcon={<ReplayIcon />}
                  aria-label={`Reintentar subida de ${jobDescription}`}
                  onClick={() => onRetry(job.id)}
                  sx={{ mt: 0.75 }}
                >
                  Reintentar
                </Button>
              ) : null}
            </Box>
          );
        })}
      </Stack>

      {pending.length > 0 ? (
        <Stack
          direction="row"
          alignItems="center"
          gap={1}
          px={2}
          py={1.25}
          bgcolor="rgba(33, 118, 255, 0.07)"
        >
          <WarningAmberRoundedIcon color="primary" fontSize="small" />
          <Typography variant="caption" fontWeight={700} color="primary.main">
            No cierres ni recargues el admin
          </Typography>
        </Stack>
      ) : null}
    </Paper>
  );
}

export default function UploadStatusPanel() {
  const { jobs, cancel, retry, dismiss } = useUploadManager();
  return (
    <UploadStatusPanelView
      jobs={jobs}
      onCancel={cancel}
      onRetry={retry}
      onDismiss={dismiss}
    />
  );
}

const panelSx = {
  position: "fixed",
  right: { xs: 12, sm: 24 },
  bottom: { xs: 12, sm: 24 },
  width: { xs: "calc(100vw - 24px)", sm: 360 },
  maxWidth: 360,
  zIndex: (theme: { zIndex: { snackbar: number } }) => theme.zIndex.snackbar + 1,
  overflow: "hidden",
  borderColor: "rgba(33, 118, 255, 0.28)",
  boxShadow: "0 18px 48px rgba(27, 44, 89, 0.22)",
};
